import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Plus, Trash2, Edit, Calendar, ShieldCheck, Check, Clock, Lock, Sparkles, Eye, EyeOff, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/use-auth";
import { friendlyError } from "@/lib/friendly-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listMyListings, upsertListing, deleteListing,
} from "@/lib/chef-listings.functions";
import { LISTING_LIMIT } from "@/lib/chef-utils";
import { requireVerifiedChef } from "@/lib/require-verified-chef";

export const Route = createFileRoute("/_authenticated/dashboard/annunci")({
  head: () => ({ meta: [{ title: "I tuoi annunci — ChefConnect" }] }),
  beforeLoad: requireVerifiedChef,
  component: ListingsPage,
});

type Listing = Awaited<ReturnType<typeof listMyListings>>[number];

function ListingsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const listFn = useServerFn(listMyListings);
  const upsertFn = useServerFn(upsertListing);
  const delFn = useServerFn(deleteListing);
  const [editing, setEditing] = useState<Partial<Listing> | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const chefQ = useQuery({
    enabled: !!user,
    queryKey: ["my-chef-verified", user?.id],
    queryFn: async () => {
      const [{ data: chef }, { data: verif }] = await Promise.all([
        supabase
          .from("chefs")
          .select("verified_badge, display_name, bio, city")
          .eq("user_id", user!.id)
          .maybeSingle(),
        supabase
          .from("verification_requests")
          .select("status")
          .eq("chef_id", user!.id)
          .order("submitted_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      return { chef, verificationStatus: (verif?.status ?? null) as string | null };
    },
  });
  const isVerified = chefQ.data?.chef?.verified_badge === true;

  const listingsQ = useQuery({ queryKey: ["my-listings"], queryFn: () => listFn() });

  const upsertMut = useMutation({
    mutationFn: (v: Partial<Listing>) =>
      upsertFn({
        data: {
          id: v.id,
          title: v.title ?? "",
          description: v.description ?? null,
          event_type: v.event_type ?? null,
          price_from: v.price_from != null ? Number(v.price_from) : null,
          is_active: v.is_active ?? true,
        },
      }),
    onSuccess: () => {
      toast.success("Annuncio aggiornato");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["my-listings"] });
      qc.invalidateQueries({ queryKey: ["my-onboarding"] });
    },
    onError: (e: Error) => toast.error(friendlyError(e)),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Annuncio rimosso dal profilo");
      qc.invalidateQueries({ queryKey: ["my-listings"] });
      qc.invalidateQueries({ queryKey: ["my-onboarding"] });
    },
    onError: (e: Error) => toast.error(friendlyError(e)),
  });

  const items = listingsQ.data ?? [];
  const canAdd = isVerified && items.length < LISTING_LIMIT;

  return (
    <div className="container mx-auto max-w-5xl px-4 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Dashboard</p>
          <h1 className="mt-1 font-display text-4xl">I tuoi annunci</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Puoi pubblicare {LISTING_LIMIT === 1 ? "1 annuncio attivo" : `${LISTING_LIMIT} annunci attivi`} ({items.length}/{LISTING_LIMIT}).
          </p>
        </div>
        {isVerified && (
          <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
            <DialogTrigger asChild>
              <Button
                onClick={() => canAdd && setEditing({ is_active: true })}
                disabled={!canAdd}
                className="bg-gold text-gold-foreground hover:bg-gold/90"
              >
                <Plus className="mr-1.5 h-4 w-4" /> Nuovo annuncio
              </Button>
            </DialogTrigger>
            <ListingDialog
              value={editing}
              onChange={setEditing}
              onSubmit={() => editing && upsertMut.mutate(editing)}
              saving={upsertMut.isPending}
            />
          </Dialog>
        )}
      </div>

      {!isVerified && !chefQ.isLoading && (
        <VerificationIncentiveCard
          profileComplete={
            !!chefQ.data?.chef?.display_name &&
            !!chefQ.data?.chef?.city &&
            (chefQ.data?.chef?.bio?.trim().length ?? 0) >= 20
          }
          verificationStatus={chefQ.data?.verificationStatus ?? null}
        />
      )}

      {isVerified && items.length >= LISTING_LIMIT && (
        <div className="mt-6 rounded-lg border border-gold/40 bg-gold-muted/20 p-4 text-sm">
          Hai già {LISTING_LIMIT === 1 ? "un annuncio attivo" : `${LISTING_LIMIT} annunci attivi`}. Modifica quello esistente per aggiornare la tua offerta.
        </div>
      )}

      {isVerified && (
      <div className="mt-8 space-y-3">
        {listingsQ.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
        {!listingsQ.isLoading && items.length === 0 && (
          <div className="overflow-hidden rounded-2xl border border-gold/40 bg-gradient-to-br from-gold-muted/40 via-card to-card p-10 text-center shadow-sm">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-gold/15 text-gold ring-1 ring-gold/30">
              <PartyPopper className="h-6 w-6" />
            </span>
            <h2 className="mt-5 font-display text-3xl">Eccellente!</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
              La tua verifica è stata approvata. Ora pubblica il tuo primo annuncio
              e inizia a ricevere richieste.
            </p>
            <Button
              onClick={() => canAdd && setEditing({ is_active: true })}
              disabled={!canAdd}
              size="lg"
              className="mt-6 bg-gold text-gold-foreground hover:bg-gold/90"
            >
              <Plus className="mr-1.5 h-4 w-4" /> Pubblica il primo annuncio
            </Button>
          </div>
        )}
        {items.map((l) => (
          <div key={l.id} className="flex items-start justify-between gap-4 rounded-xl border border-border bg-card p-5">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-xl">{l.title}</h3>
                {l.is_active
                  ? <span className="rounded-full bg-gold-muted/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-gold">Pubblicato</span>
                  : <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider">Nascosto</span>}
              </div>
              {l.event_type && <p className="mt-1 text-xs uppercase tracking-wider text-gold">{l.event_type}</p>}
              {l.description && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{l.description}</p>}
              {l.price_from != null && <p className="mt-2 text-sm">da <strong>€{l.price_from}</strong> a persona</p>}
            </div>
            <div className="flex gap-1.5">
              <Button
                variant="ghost"
                size="icon"
                aria-label={l.is_active ? "Nascondi annuncio" : "Pubblica annuncio"}
                title={l.is_active ? "Nascondi temporaneamente" : "Pubblica"}
                disabled={upsertMut.isPending}
                onClick={() => upsertMut.mutate({ ...l, is_active: !l.is_active })}
              >
                {l.is_active ? <Eye className="h-4 w-4 text-gold" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
              </Button>
              <Button variant="ghost" size="icon" aria-label="Modifica annuncio" onClick={() => setEditing(l)}><Edit className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => setDeletingId(l.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      )}

      <AlertDialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'annuncio?</AlertDialogTitle>
            <AlertDialogDescription>
              L'annuncio sarà rimosso definitivamente. L'operazione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (deletingId) { delMut.mutate(deletingId); setDeletingId(null); } }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ListingDialog({
  value, onChange, onSubmit, saving,
}: {
  value: Partial<Listing> | null;
  onChange: (v: Partial<Listing> | null) => void;
  onSubmit: () => void;
  saving: boolean;
}) {
  if (!value) return null;
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{value.id ? "Modifica annuncio" : "Nuovo annuncio"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Titolo *</Label>
          <Input
            value={value.title ?? ""}
            onChange={(e) => onChange({ ...value, title: e.target.value })}
            placeholder="Es. Cena romantica con menu degustazione 5 portate"
            maxLength={120}
          />
        </div>
        <div>
          <Label>Tipologia evento</Label>
          <Input
            value={value.event_type ?? ""}
            onChange={(e) => onChange({ ...value, event_type: e.target.value })}
            placeholder="Cena privata, Show cooking, Catering…"
            maxLength={60}
          />
        </div>
        <div>
          <Label>Descrizione</Label>
          <Textarea
            rows={4}
            value={value.description ?? ""}
            onChange={(e) => onChange({ ...value, description: e.target.value })}
            placeholder="Cosa offri, quante portate, ingredienti, durata, ecc."
            maxLength={2000}
          />
        </div>
        <div>
          <Label>Prezzo a partire da (€/persona)</Label>
          <Input
            type="number" min={0} max={10000}
            value={value.price_from ?? ""}
            onChange={(e) => onChange({ ...value, price_from: e.target.value ? Number(e.target.value) : null })}
          />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <Label className="text-sm">Pubblicato</Label>
            <p className="text-xs text-muted-foreground">Disattiva per nascondere temporaneamente l'annuncio</p>
          </div>
          <Switch
            checked={value.is_active ?? true}
            onCheckedChange={(c) => onChange({ ...value, is_active: c })}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onChange(null)}>Annulla</Button>
        <Button onClick={onSubmit} disabled={saving || !(value.title ?? "").trim()} className="bg-gold text-gold-foreground hover:bg-gold/90">
          {saving ? "Salvo…" : "Salva"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/**
 * Tempo medio dichiarato per l'approvazione della verifica. Modificare qui
 * se il tempo medio reale dovesse cambiare in futuro.
 */
const VERIFICATION_AVG_TIME_LABEL = "meno di 24 ore";

type VerificationStatus = "pending" | "approved" | "rejected" | string | null;

function VerificationIncentiveCard({
  profileComplete,
  verificationStatus,
}: {
  profileComplete: boolean;
  verificationStatus: VerificationStatus;
}) {
  const isPending = verificationStatus === "pending";
  const steps = [
    {
      label: "Profilo completato",
      done: profileComplete,
      current: !profileComplete,
    },
    {
      label: isPending ? "Verifica identità in corso" : "Verifica identità",
      done: false,
      current: profileComplete,
      pending: isPending,
    },
    {
      label: "Pubblica il primo annuncio",
      done: false,
      current: false,
      locked: true,
    },
    {
      label: "Ricevi richieste dai clienti",
      done: false,
      current: false,
      locked: true,
    },
  ];

  const benefits = [
    "Pubblica il tuo annuncio",
    "Compari nelle ricerche pubbliche",
    'Ottieni il badge "Chef Verificato"',
    "Aumenti la fiducia dei clienti",
    "Ricevi richieste direttamente dal tuo profilo",
  ];

  return (
    <div className="mt-8 overflow-hidden rounded-2xl border border-gold/40 bg-gradient-to-br from-gold-muted/30 via-card to-card shadow-sm">
      <div className="p-6 sm:p-10">
        <div className="flex items-start gap-4">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-gold/15 text-gold ring-1 ring-gold/30">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider text-gold">
              <Sparkles className="h-3 w-3" /> Prossimo passo
            </span>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl">
              Verifica il tuo profilo per iniziare a ricevere richieste
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
              Per proteggere clienti e chef, tutti i profili vengono verificati prima
              della pubblicazione degli annunci. Una volta approvata la verifica potrai
              pubblicare il tuo annuncio e iniziare a ricevere richieste.
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          {/* Benefici */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Cosa sblocchi con la verifica
            </p>
            <ul className="mt-3 space-y-2.5">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-gold/15 text-gold">
                    <Check className="h-3 w-3" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Avanzamento */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Il tuo percorso
            </p>
            <ol className="mt-3 space-y-3">
              {steps.map((s, i) => {
                const StepIcon = s.done
                  ? Check
                  : s.pending
                    ? Clock
                    : s.locked
                      ? Lock
                      : ShieldCheck;
                const tone = s.done
                  ? "bg-gold/15 text-gold ring-gold/30"
                  : s.pending
                    ? "bg-gold/10 text-gold ring-gold/30 animate-pulse"
                    : s.current
                      ? "bg-foreground/10 text-foreground ring-foreground/20"
                      : "bg-muted text-muted-foreground ring-border";
                return (
                  <li key={i} className="flex items-center gap-3">
                    <span
                      className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ring-1 ${tone}`}
                    >
                      <StepIcon className="h-3.5 w-3.5" />
                    </span>
                    <span
                      className={`text-sm ${s.locked ? "text-muted-foreground" : "text-foreground"}`}
                    >
                      {s.label}
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            asChild
            size="lg"
            className="bg-gold text-gold-foreground hover:bg-gold/90"
          >
            <Link to="/dashboard/verifica">
              {isPending ? "Controlla lo stato della verifica" : "Completa la verifica"}
            </Link>
          </Button>
          <p className="text-xs text-muted-foreground sm:max-w-sm sm:text-right">
            La verifica richiede generalmente {VERIFICATION_AVG_TIME_LABEL}. Appena
            approvata potrai pubblicare il tuo primo annuncio e iniziare a ricevere
            richieste dai clienti.
          </p>
        </div>
      </div>
    </div>
  );
}